import dayjs, { Dayjs } from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import {
  RegisterBanksSelect,
  mapPaymentDirectionToBankType,
} from '@/modules/common/components/RegisterBanksSelect.tsx';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import {
  PaymentDividendOption,
  PaymentDividendSelect,
} from '@/modules/issuer-dashboard/components/PaymentDividendSelect.tsx';
import {
  PaymentTransactionOption,
  PaymentTransactionSelect,
} from '@/modules/issuer-dashboard/components/PaymentTransactionSelect.tsx';
import { useGetRegister } from '@/modules/issuer-dashboard/hooks/useGetRegister.tsx';
import { useCreatePaymentMutation } from '@/modules/issuer-dashboard/queries/payments.tsx';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginateOption,
  SelectWithCreatableOption,
  TextInputField,
  getSelectOptionByEnum,
} from 'ui';
import {
  CreatePaymentRequest,
  CurrencyEnum,
  PaymentDirection,
  PaymentStatus,
  RegisterBankExtended,
  TransactionType,
} from 'api';

const validationSchema = yup
  .object<CreatePaymentRequest>({
    shareholder_id: yup.number().when(['direction'], ([direction], schema) => {
      return direction === PaymentDirection.InvestorToFund ||
        direction === PaymentDirection.FundToInvestor
        ? schema
            .typeError('Shareholder id must be a number')
            .required('This field is required')
            .moreThan(0, 'This field is required')
        : schema.nullable();
    }),
    status: yup.mixed<PaymentStatus>().oneOf(Object.values(PaymentStatus)),
    direction: yup
      .mixed<PaymentDirection>()
      .oneOf(Object.values(PaymentDirection))
      .required('This field is required'),
    amount: yup
      .number()
      .typeError('Amount must be a number')
      .moreThan(0, 'Amount must be more than 0')
      .required('This field is required'),
    memo: yup.string().trim(),
    requested_at_date: yup.string().trim().required('This field is required'),
    confirmation_date: yup
      .string()
      .trim()
      .when(['status', 'requested_at_date'], ([status], schema) => {
        return status === PaymentStatus.Completed
          ? schema
              .required('This field is required')
              .test('', "Confirmation date can't be before requested date", function (value) {
                const { requested_at_date } = this.parent;
                return requested_at_date && value
                  ? dayjs(value).isAfter(dayjs(requested_at_date)) ||
                      dayjs(value).isSame(dayjs(requested_at_date), 'day')
                  : true;
              })
          : schema.nullable();
      }),
    from_bank_id: yup.number().when(['direction'], ([direction], schema) => {
      return direction === PaymentDirection.FundToInvestor
        ? schema.moreThan(0, 'This field is required').required('This field is required')
        : schema.nullable();
    }),
    to_bank_id: yup.number().when(['direction'], ([direction], schema) => {
      return direction === PaymentDirection.InvestorToFund
        ? schema.moreThan(0, 'This field is required').required('This field is required')
        : schema.nullable();
    }),
    fx_rate: yup.number().nullable(),
    transaction_id: yup.number().nullable(),
    dividend_event_id: yup.number().nullable(),
  })
  .test(
    'transaction-or-dividend-required',
    'Select transaction or distribution',
    function (values) {
      const formValues = (values || {}) as CreatePaymentRequest;
      const hasTransaction = !!formValues.transaction_id && formValues.transaction_id > 0;
      const hasDividend = !!formValues.dividend_event_id && formValues.dividend_event_id > 0;

      if (hasTransaction && hasDividend) {
        return this.createError({
          path: 'transaction_id',
          message: 'Select either transaction or distribution',
        });
      }

      if (hasTransaction || hasDividend) {
        return true;
      }

      return this.createError({
        path: 'transaction_id',
        message: 'Select transaction or distribution',
      });
    },
  );

const initialValues: CreatePaymentRequest = {
  shareholder_id: 0,
  status: undefined,
  direction: undefined,
  amount: 0,
  memo: '',
  requested_at_date: undefined,
  confirmation_date: undefined,
  from_bank_id: undefined,
  to_bank_id: undefined,
  transaction_id: undefined,
  dividend_event_id: undefined,
  is_preview_distribution: undefined,
  fx_rate: undefined,
};

const statusOptions = getSelectOptionByEnum<PaymentStatus>(PaymentStatus).filter(
  ({ value }) => value !== PaymentStatus.Unsuccessfull,
);

interface IssuerAddPaymentModalProps extends BaseModalProps {
  shareholderId?: number;
}

export const IssuerAddPaymentModal = ({
  open,
  onClose,
  shareholderId,
}: IssuerAddPaymentModalProps) => {
  const registerId = useGetRegister()?.id || null;
  const { mutateAsync } = useCreatePaymentMutation();
  const [fromCurrency, setFromCurrency] = useState<CurrencyEnum>();
  const [toCurrency, setToCurrency] = useState<CurrencyEnum>();
  const [selectedTransaction, setSelectedTransaction] = useState<PaymentTransactionOption | null>(
    null,
  );
  const [selectedDividend, setSelectedDividend] = useState<PaymentDividendOption | null>(null);
  // const [isFromFinancialModelingPrep, setIsFromFinancialModelingPrep] = useState(false);

  const formik = useFormik<CreatePaymentRequest>({
    initialValues: {
      ...initialValues,
      shareholder_id: shareholderId ?? initialValues.shareholder_id,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await mutateAsync(values);
      resetForm();
      setSelectedTransaction(null);
      setSelectedDividend(null);
      onClose();
    },
    enableReinitialize: true,
  });

  const { data: registerShareholderList, isLoading: isRegisterShareholderListLoading } =
    useRegisterShareholderList({ register_id: registerId as number }, { disabled: !registerId });

  const registerShareholderListOptions = useMemo<SelectOption<number>[]>(() => {
    if (!registerShareholderList?.shareholders) return [];

    return registerShareholderList?.shareholders.map(item => ({
      label: item.entity.entity_name,
      value: item.id,
    }));
  }, [registerShareholderList]);

  const handleChangeShareholder = (id: number | null) => {
    formik.setFieldValue('shareholder_id', id);
    formik.setFieldError('shareholder_id', '');
  };

  const handleChangeDirection = useCallback(
    (direction: PaymentDirection | null) => {
      formik.setFieldValue('direction', direction);
      formik.setFieldError('direction', '');
      formik.setFieldValue('transaction_id', undefined);
      formik.setFieldError('transaction_id', '');
      setSelectedTransaction(null);

      if (direction === PaymentDirection.InvestorToFund) {
        formik.setFieldValue('from_bank_id', undefined);
        formik.setFieldError('from_bank_id', '');
      }
      if (direction === PaymentDirection.FundToInvestor) {
        formik.setFieldValue('to_bank_id', undefined);
        formik.setFieldError('to_bank_id', '');
      }
    },
    [formik],
  );

  const handleChangeStatus = useCallback(
    (status: PaymentStatus | null) => {
      formik.setFieldValue('status', status);
      formik.setFieldError('status', '');
      if (status === PaymentStatus.Pending) formik.setFieldValue('confirmation_date', null);
      if (status === PaymentStatus.Completed)
        formik.setFieldValue('confirmation_date', formik.values.requested_at_date);
    },
    [formik.values.requested_at_date],
  );

  const handleChangeAmount = (amount: string | number) => {
    formik.setFieldValue('amount', !isNaN(+amount) ? +amount : undefined);
    formik.setFieldError('amount', '');
  };

  const handleChangeDate = (
    field: 'requested_at_date' | 'confirmation_date',
    date: Dayjs | null,
  ) => {
    formik.setFieldValue(field, date ? date.toISOString() : date);
    formik.setFieldError(field, '');
  };

  const handleChangeBank = useCallback(
    (fieldName: keyof CreatePaymentRequest) =>
      (item: SelectPaginateOption<RegisterBankExtended> | null) => {
        formik.setFieldValue(fieldName, item?.value.id);
        formik.setFieldError(fieldName, '');
        if (fieldName === 'to_bank_id') {
          setToCurrency(item?.value.bank_currency);
        }
        if (fieldName === 'from_bank_id') {
          setFromCurrency(item?.value.bank_currency);
        }
        setTimeout(() => {
          if (toCurrency && toCurrency === fromCurrency) {
            formik.setFieldValue('fx_rate', 1);
            formik.setFieldError('fx_rate', '');
          }
        }, 1);
      },
    [toCurrency, fromCurrency],
  );

  const handleChangeTransaction = useCallback((option: PaymentTransactionOption | null) => {
    setSelectedTransaction(option);
    if (option) {
      setSelectedDividend(null);
      formik.setFieldValue('dividend_event_id', undefined);
    }
    formik.setFieldValue('transaction_id', option?.value.id);
    formik.setFieldError('transaction_id', '');
    formik.setFieldError('dividend_event_id', '');
  }, []);

  const handleChangeDividend = useCallback((option: PaymentDividendOption | null) => {
    setSelectedDividend(option);
    if (option) {
      setSelectedTransaction(null);
      formik.setFieldValue('transaction_id', undefined);
    }
    formik.setFieldValue('dividend_event_id', option?.value.id);
    formik.setFieldError('dividend_event_id', '');
    formik.setFieldError('transaction_id', '');
  }, []);

  const directionOptions = useMemo<SelectWithCreatableOption<PaymentDirection, string>[]>(() => {
    const items = getSelectOptionByEnum<PaymentDirection>(PaymentDirection);

    return items;
  }, []);

  const transactionTypeExclude = useMemo(() => {
    if (formik.values.direction === PaymentDirection.FundToInvestor) {
      return Object.values(TransactionType).filter(
        type => type !== TransactionType.Redemption && type !== TransactionType.BuyBack,
      );
    }

    if (formik.values.direction === PaymentDirection.InvestorToFund) {
      return Object.values(TransactionType).filter(
        type => type !== TransactionType.Subscription && type !== TransactionType.OpeningBalance,
      );
    }

    return undefined;
  }, [formik.values.direction]);

  return (
    <ModalContentWrapper open={open} title="Add Payment" onClose={onClose}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-6" onSubmit={preventFormDefault}>
          <Select
            label="Direction"
            options={directionOptions}
            value={formik.values.direction}
            onChange={handleChangeDirection}
            error={formik.errors.direction}
            isClearable={true}
          />

          {formik.values.direction === PaymentDirection.FundToInvestor && (
            <RegisterBanksSelect
              onChange={handleChangeBank('from_bank_id')}
              value={formik.values.from_bank_id}
              label="From Bank"
              key={`From_Bank_direction_${formik.values.direction}`}
              clearable={true}
              error={formik.errors.from_bank_id}
              needBankType={
                mapPaymentDirectionToBankType[formik.values.direction as PaymentDirection]
              }
              excludeIds={formik.values.to_bank_id ? [formik.values.to_bank_id] : undefined}
              is_overall={true}
            />
          )}
          {formik.values.direction === PaymentDirection.InvestorToFund && (
            <RegisterBanksSelect
              onChange={handleChangeBank('to_bank_id')}
              value={formik.values.to_bank_id}
              label="To Bank"
              key={`To_Bank_direction_${formik.values.direction}`}
              clearable={true}
              error={formik.errors.to_bank_id}
              needBankType={
                mapPaymentDirectionToBankType[formik.values.direction as PaymentDirection]
              }
              excludeIds={formik.values.from_bank_id ? [formik.values.from_bank_id] : undefined}
              prefillBank={formik.values.direction === PaymentDirection.InvestorToFund}
              is_overall={true}
            />
          )}
          {(formik.values.direction === PaymentDirection.FundToInvestor ||
            formik.values.direction === PaymentDirection.InvestorToFund) && (
            <Select
              label="Shareholder"
              options={registerShareholderListOptions}
              isLoading={isRegisterShareholderListLoading}
              value={formik.values.shareholder_id}
              onChange={handleChangeShareholder}
              error={formik.errors.shareholder_id}
            />
          )}

          <CalendarInput
            label="Requested date"
            onChange={handleChangeDate.bind(null, 'requested_at_date')}
            error={formik.errors.requested_at_date}
            value={formik.values.requested_at_date ? dayjs(formik.values.requested_at_date) : null}
          />

          <CurrencyInput
            label={
              <span>
                Amount{' '}
                {toCurrency && (
                  <>
                    {' '}
                    <span className="font-bold font-italic">({toCurrency.toUpperCase()})</span>
                  </>
                )}
              </span>
            }
            error={formik.errors.amount}
            value={formik.values.amount}
            onChange={handleChangeAmount}
          />

          <PaymentTransactionSelect
            value={selectedTransaction}
            onChange={handleChangeTransaction}
            error={formik.errors.transaction_id}
            typeExclude={transactionTypeExclude}
            disabled={!formik.values.direction}
            shareholderId={shareholderId}
          />

          {formik.values.direction === PaymentDirection.FundToInvestor && (
            <PaymentDividendSelect
              value={selectedDividend}
              onChange={handleChangeDividend}
              error={formik.errors.dividend_event_id}
              disabled={!formik.values.direction}
            />
          )}

          <Select
            label="Status"
            options={statusOptions}
            value={formik.values.status}
            onChange={handleChangeStatus}
            error={formik.errors.status}
          />

          {formik.values.status === PaymentStatus.Completed && (
            <CalendarInput
              label="Confirmation date"
              onChange={handleChangeDate.bind(null, 'confirmation_date')}
              error={formik.errors.confirmation_date}
              value={
                formik.values.confirmation_date ? dayjs(formik.values.confirmation_date) : null
              }
            />
          )}

          <TextInputField label="Memo (Optional)" name="memo" placeholder="Memo..." />
          <Button onClick={formik.submitForm} loading={formik.isSubmitting}>
            Confirm
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
